/** WorkspaceRuntime projects the Workspace object manager for UI consumers. */
import { createSnapshotStore } from "../contract/store.js";
import { WorkspaceManager } from "./manager.js";
/** Structured create failure for UI flows that distinguish Host business errors. */
export class WorkspaceCreateError extends Error {
    rpcError;
    constructor(rpcError) {
        super(`workspace create failed: ${rpcError.code}: ${rpcError.message}`);
        this.rpcError = rpcError;
        this.name = 'WorkspaceCreateError';
    }
}
/** Structured browse failure so the directory browser can branch on Host business codes. */
export class DirectoryBrowseError extends Error {
    rpcError;
    constructor(rpcError) {
        super(`directory browse failed: ${rpcError.code}: ${rpcError.message}`);
        this.rpcError = rpcError;
        this.name = 'DirectoryBrowseError';
    }
}
/** Real Workspace object layer and Host actions. */
export class WorkspaceRuntime {
    api;
    sessions;
    /** UI-facing immutable projection; the manager remains wire truth. */
    list;
    /** Workspace baseline and frame owner. */
    manager;
    /** In-flight blank-session creates keyed by workspace (connectWorkspace coalescing). */
    connecting = new Map();
    /** Guards the runtime-owned one-shot initial-selection subscription. */
    initialSelectionStarted = false;
    /**
     * @param ctx - client root context.
     * @param api - shared wire client.
     * @param sessions - cross-domain sessions face used for recency and blank-session reuse.
     */
    constructor(ctx, api, sessions) {
        this.api = api;
        this.sessions = sessions;
        this.manager = new WorkspaceManager(api);
        this.list = createSnapshotStore({
            items: [], archivedSessionIds: [], groups: [], state: 'idle', phase: 'pending', error: null,
            baselinesReady: false, recentWorkspaceId: undefined,
        });
        this.manager.subscribe(() => { this.project(); });
        this.sessions.list.subscribe(() => { this.project(); });
        ctx.reflect.provide('workspaces', this, undefined);
    }
    /**
     * Resolve the session a New Session flow lands in once this Workspace is
     * chosen: reuse the workspace's existing blank session when one is in the
     * list mirror, else create a fresh one on the host (`session.create` births
     * the full Session+Agent — the client holds no intermediate state). The
     * caller owns navigation: take the returned id to `sessions.open`.
     * Resolution guarantee (both arms): the returned id is already in the list
     * store and `sessions.binding(id)` resolves synchronously — draft hand-off
     * may write the new scope's machine before opening.
     * @param workspaceId - chosen Workspace (must be in the workspace list).
     * @returns the reused or newly created session id.
     */
    async connectWorkspace(workspaceId) {
        const workspace = this.list.getSnapshot().items.find(item => item.workspaceId === workspaceId);
        if (workspace === undefined)
            throw new Error(`workspaces.connectWorkspace: unknown workspace ${workspaceId}`);
        // Coalesce concurrent connects: a create's summary lands without cwd
        // until the host frame arrives, so a second call inside that window
        // would miss the reuse scan and mint another hidden blank session.
        const inflight = this.connecting.get(workspaceId);
        if (inflight !== undefined)
            return inflight;
        // Reuse requires workspace membership (id in sessionIds AND same
        // canonical cwd — the host's own membership rule), never cwd alone:
        // a cwd match can belong to no account (sessions the CLI/TUI birthed at
        // the host cwd, or a deleted/recreated registration) and reusing it
        // would open a session no grouping surface shows under this workspace.
        // An archived blank is never reused either: reuse would open a session
        // no grouping surface can show, so New Session mints a fresh one instead.
        const archived = this.list.getSnapshot().archivedSessionIds;
        const sessions = this.sessions.list.getSnapshot();
        for (const id of sessions.ids) {
            const summary = sessions.byId[id];
            // `blank` seul ne suffit PAS (voir SessionsPortSummary.title) : un titre
            // prouve qu'un premier message est parti, donc que la conversation
            // n'est pas neuve — quoi qu'en dise le drapeau.
            const untouched = summary !== undefined
                && (summary.title === undefined || summary.title === '');
            if (summary !== undefined && summary.blank && untouched && summary.cwd === workspace.path
                && workspace.sessionIds.includes(summary.id)
                && !archived.includes(summary.id))
                return summary.id;
        }
        const attempt = this.sessions.create({ workspaceId })
            .then((sessionId) => {
            // L'appartenance hôte est acquise (create résolu = attach fait) ; le
            // frame workspace-changed peut tarder — groupe la session dès
            // maintenant pour que chip, sidebar et réutilisation la voient dans
            // le workspace choisi sans dépendre du frame.
            this.manager.noteSessionMembership(workspaceId, sessionId);
            return sessionId;
        })
            .finally(() => { this.connecting.delete(workspaceId); });
        this.connecting.set(workspaceId, attempt);
        return attempt;
    }
    /**
     * Follow the first complete Workspace/Session baseline and select a default
     * session exactly once. A restored current session wins; otherwise the most
     * recent Workspace is connected (reusing or creating its blank session).
     * Later explicit clears stay cleared instead of retriggering this startup
     * policy. A failed connect may retry on the next baseline projection.
     * @returns disposer for the baseline subscription; late work cannot navigate after disposal.
     */
    startInitialSelection() {
        if (this.initialSelectionStarted) {
            throw new Error('workspaces.startInitialSelection: already started');
        }
        this.initialSelectionStarted = true;
        let state = 'waiting';
        let disposed = false;
        const reconcile = () => {
            if (disposed || state !== 'waiting')
                return;
            const workspace = this.list.getSnapshot();
            if (!workspace.baselinesReady)
                return;
            const current = this.sessions.list.getSnapshot().current;
            const target = workspace.recentWorkspaceId;
            if (current !== undefined || target === undefined) {
                state = 'done';
                return;
            }
            state = 'connecting';
            void this.connectWorkspace(target).then((sessionId) => {
                if (disposed)
                    return;
                if (this.sessions.list.getSnapshot().current === undefined) {
                    this.sessions.open(sessionId);
                }
                state = 'done';
            }, (reason) => {
                if (disposed)
                    return;
                state = 'waiting';
                console.warn('initial workspace selection failed:', reason);
            });
        };
        const unsubscribe = this.list.subscribe(reconcile);
        reconcile();
        return () => {
            disposed = true;
            unsubscribe();
        };
    }
    /**
     * The shared New Session action behind the shell entry points (sidebar
     * button, workspace browser): resolve the target Workspace — explicit wins,
     * then the current Session's Workspace, then the recent-Workspace
     * projection — connect its blank session and navigate there; with no
     * Workspace at all, clear the selection into the New Session view state.
     * Connect failures are non-fatal (console diagnostics; the current view
     * stays usable).
     * @param workspaceId - explicit target Workspace for scoped actions.
     */
    startSession(workspaceId) {
        const workspace = this.list.getSnapshot();
        const current = this.sessions.list.getSnapshot().current;
        const currentWorkspaceId = current === undefined
            ? undefined
            : workspace.items.find(item => item.sessionIds.includes(current))?.workspaceId;
        const target = workspaceId ?? currentWorkspaceId ?? workspace.recentWorkspaceId;
        if (target === undefined) {
            this.sessions.clear();
            return;
        }
        void this.connectWorkspace(target).then((sessionId) => { this.sessions.open(sessionId); }, (reason) => { console.warn('new session failed:', reason); });
    }
    /**
     * Register an existing path as a Workspace.
     * @param input - the Host create payload.
     * @returns the created or idempotently resolved Workspace.
     */
    async create(input) {
        const result = await this.manager.create(input);
        if (!result.ok)
            throw new WorkspaceCreateError(result.error);
        return result.value.workspace;
    }
    /**
     * Open the Host's native directory picker (the `native` capability).
     * @returns the selected path, or null when the user cancelled.
     */
    async pickDirectory() {
        const response = await this.api.host.pickDirectory({});
        if (!response.result.ok) {
            throw new Error(`directory picker failed: ${response.result.error.message}`);
        }
        return response.result.value.path;
    }
    /**
     * List one directory level through the Host's `browse` capability.
     * @param path - absolute directory to list; absent lists the Host home directory.
     * @param signal - aborts the wire request (and the Host's scan) when the caller supersedes it.
     * @returns the level's listing with breadcrumb ancestry.
     */
    async listDirectory(path, signal) {
        const response = await this.api.host.listDirectory(path === undefined ? {} : { path }, signal);
        if (!response.result.ok)
            throw new DirectoryBrowseError(response.result.error);
        return response.result.value;
    }
    /**
     * Create one child directory through the Host's `browse` capability.
     * @param path - absolute existing parent directory.
     * @param name - single non-blank path segment.
     * @returns the created directory's absolute path.
     */
    async createDirectory(path, name) {
        const response = await this.api.host.createDirectory({ path, name });
        if (!response.result.ok)
            throw new DirectoryBrowseError(response.result.error);
        return response.result.value.path;
    }
    /**
     * Open a filesystem path with the Host operating system's default application.
     * @param path - absolute or host-resolvable path.
     */
    async openPath(path) {
        const response = await this.api.host.openPath({ path });
        if (!response.result.ok) {
            throw new Error(`path open failed: ${response.result.error.message}`);
        }
    }
    /**
     * Rename a Workspace.
     * @param workspaceId - target workspace.
     * @param title - new display title (trimmed non-empty by the Host).
     * @returns the renamed Workspace view.
     */
    async rename(workspaceId, title) {
        const result = await this.manager.rename(workspaceId, title);
        if (!result.ok)
            throw new Error(`workspace rename failed: ${result.error.code}: ${result.error.message}`);
        return result.value.workspace;
    }
    /**
     * Delete one Workspace registration. Sessions, session logs, and the
     * directory remain Host-owned outside this operation.
     * @param workspaceId - target workspace.
     */
    async delete(workspaceId) {
        const result = await this.manager.delete(workspaceId);
        if (!result.ok)
            throw new Error(`workspace delete failed: ${result.error.code}: ${result.error.message}`);
    }
    /**
     * Move a Workspace within the durable registry display order.
     * @param workspaceId - Workspace to move.
     * @param beforeWorkspaceId - Anchor workspace; omitted appends.
     */
    async insertBefore(workspaceId, beforeWorkspaceId) {
        const result = await this.manager.insertBefore(workspaceId, beforeWorkspaceId);
        if (!result.ok)
            throw new Error(`workspace reorder failed: ${result.error.code}: ${result.error.message}`);
    }
    /**
     * Create an empty workspace group.
     * @param title - display title, unique among the account's groups.
     * @returns the new group's id.
     */
    async createGroup(title) {
        const result = await this.manager.createGroup(title);
        if (!result.ok)
            throw new Error(`workspace group create failed: ${result.error.code}: ${result.error.message}`);
        return result.value.groups.find(group => group.title === title.trim())?.groupId;
    }
    /**
     * Rename one workspace group.
     * @param groupId - group to rename.
     * @param title - new display title.
     */
    async renameGroup(groupId, title) {
        const result = await this.manager.renameGroup(groupId, title);
        if (!result.ok)
            throw new Error(`workspace group rename failed: ${result.error.code}: ${result.error.message}`);
    }
    /**
     * Delete one workspace group; its workspaces are ungrouped, never deleted.
     * @param groupId - group to delete.
     */
    async deleteGroup(groupId) {
        const result = await this.manager.deleteGroup(groupId);
        if (!result.ok)
            throw new Error(`workspace group delete failed: ${result.error.code}: ${result.error.message}`);
    }
    /**
     * Put a workspace in a group, or take it out of any group.
     * @param workspaceId - workspace to move.
     * @param groupId - target group; omitted ungroups.
     */
    async setGroup(workspaceId, groupId) {
        const result = await this.manager.setGroup(workspaceId, groupId);
        if (!result.ok)
            throw new Error(`workspace grouping failed: ${result.error.code}: ${result.error.message}`);
    }
    /**
     * Archive a session into the registry-global set. Clearing an archived
     * current selection is the projection sweep's job (one rule for the local
     * echo and a remote tab's frame alike).
     * @param sessionId - session to archive.
     */
    async archiveSession(sessionId) {
        const result = await this.manager.archiveSession(sessionId);
        if (!result.ok)
            throw new Error(`session archive failed: ${result.error.code}: ${result.error.message}`);
    }
    /**
     * Move a session within its Workspace's manual order (DOM-insertBefore-like).
     * @param workspaceId - owning workspace.
     * @param sessionId - accounted session to move.
     * @param beforeSessionId - accounted anchor to insert before; omitted appends.
     * @returns the updated Workspace view.
     */
    async insertSessionBefore(workspaceId, sessionId, beforeSessionId) {
        const result = await this.manager.insertSessionBefore(workspaceId, sessionId, beforeSessionId);
        if (!result.ok)
            throw new Error(`workspace move failed: ${result.error.code}: ${result.error.message}`);
        return result.value.workspace;
    }
    /**
     * Refresh the workspace baseline, reusing an in-flight pull.
     * @returns completion of the current or newly started workspace baseline pull.
     */
    refresh() {
        return this.manager.refresh();
    }
    /**
     * Route a Host stream envelope into the Workspace object layer.
     * @param envelope - validated Host stream envelope.
     */
    handleHostEnvelope(envelope) {
        this.manager.handleHostEnvelope(envelope);
    }
    /** Rebuild the Workspace baseline after connection. */
    handleConnected() {
        this.manager.handleConnected();
    }
    project() {
        const workspace = this.manager.getSnapshot();
        const sessions = this.sessions.list.getSnapshot();
        const baselinesReady = workspace.phase === 'ready' && sessions.phase === 'ready';
        // An archived current selection clears into the New Session view state —
        // a hidden row must not stay open behind the list. Sweeping here covers
        // every install path with one rule: the local unary echo, another tab's
        // changed frame, and a reconnect baseline restoring a persisted
        // selection that was archived while this client was away.
        if (sessions.current !== undefined && workspace.archivedSessionIds.includes(sessions.current)) {
            this.sessions.clear();
        }
        this.list.set({
            items: workspace.items,
            archivedSessionIds: workspace.archivedSessionIds,
            groups: workspace.groups,
            state: workspace.state,
            phase: workspace.phase,
            error: workspace.error,
            baselinesReady,
            recentWorkspaceId: baselinesReady ? recentWorkspace(workspace.items, sessions.byId) : undefined,
        });
    }
}
/** Stable tie-breaking follows Host Workspace order. */
function recentWorkspace(workspaces, sessions) {
    let selected;
    let selectedTime = Number.NEGATIVE_INFINITY;
    for (const workspace of workspaces) {
        let latest = Number.NEGATIVE_INFINITY;
        for (const sessionId of workspace.sessionIds) {
            const session = sessions[sessionId];
            if (session !== undefined)
                latest = Math.max(latest, session.updatedAt);
        }
        if (latest === Number.NEGATIVE_INFINITY)
            latest = Date.parse(workspace.createdAt);
        if (selected === undefined || latest > selectedTime) {
            selected = workspace.workspaceId;
            selectedTime = latest;
        }
    }
    return selected;
}
//# sourceMappingURL=service.js.map